import { db } from '../../../../config/firebaseConfig';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { normalizeSkillIcon } from '../../../Common/Icons/iconOptions.js';

const normalizeSkillData = (skillData) => {
  const normalized = { ...skillData };
  if (Object.prototype.hasOwnProperty.call(skillData || {}, 'icon')) {
    normalized.icon = normalizeSkillIcon(skillData.icon);
  }
  return normalized;
};

// ==================== SKILL MANAGEMENT FUNCTIONS ====================

/**
 * Save a new skill to Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} skillId - Skill unique ID
 * @param {Object} skillData - Skill data (name, description, rating)
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if save fails
 */
export const saveSkill = async (userId, skillId, skillData) => {
  try {
    const skillRef = doc(db, 'users', userId, 'skills', skillId);

    await setDoc(skillRef, {
      ...normalizeSkillData(skillData),
      id: skillId,
      createdAt: new Date(),
      updatedAt: new Date(),
      isPublished: false,
      publicRefsCount: 0,
    }, { merge: true });

    return {
      success: true,
      message: `Skill "${skillData.name}" saved successfully`,
    };
  } catch (error) {
    console.error('Error saving skill:', error.message);
    throw new Error(`Failed to save skill: ${error.message}`);
  }
};

/**
 * Load a specific skill from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} skillId - Skill ID to load
 * @returns {Promise<Object|null>} - Skill data or null if not found
 * @throws {Error} - Firestore error if load fails
 */
export const loadSkill = async (userId, skillId) => {
  try {
    const skillRef = doc(db, 'users', userId, 'skills', skillId);
    const docSnap = await getDoc(skillRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
      };
    }

    return null;
  } catch (error) {
    console.error('Error loading skill:', error.message);
    throw new Error(`Failed to load skill: ${error.message}`);
  }
};

/**
 * Load all skills for a specific user
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<Array>} - Array of skill objects with metadata
 * @throws {Error} - Firestore error if load fails
 */
export const loadUserSkills = async (userId) => {
  try {
    const skillsRef = collection(db, 'users', userId, 'skills');
    const querySnapshot = await getDocs(skillsRef);

    const skills = [];
    querySnapshot.forEach((doc) => {
      skills.push({
        ...doc.data(),
        id: doc.id,
      });
    });

    // Sort by createdAt in descending order (newest first)
    skills.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB - dateA;
    });

    return skills;
  } catch (error) {
    console.error('Error loading skills:', error.message);
    throw new Error(`Failed to load skills: ${error.message}`);
  }
};

/**
 * Update an existing skill in Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} skillId - Skill ID to update
 * @param {Object} updateData - Partial data to update
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if update fails
 */
export const updateSkill = async (userId, skillId, updateData) => {
  try {
    const skillRef = doc(db, 'users', userId, 'skills', skillId);

    await updateDoc(skillRef, {
      ...normalizeSkillData(updateData),
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: 'Skill updated successfully',
    };
  } catch (error) {
    console.error('Error updating skill:', error.message);
    throw new Error(`Failed to update skill: ${error.message}`);
  }
};

/**
 * Delete a specific skill from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} skillId - Skill ID to delete
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if delete fails
 */
export const deleteSkill = async (userId, skillId) => {
  try {
    const skillRef = doc(db, 'users', userId, 'skills', skillId);
    await deleteDoc(skillRef);

    return {
      success: true,
      message: 'Skill deleted successfully',
    };
  } catch (error) {
    console.error('Error deleting skill:', error.message);
    throw new Error(`Failed to delete skill: ${error.message}`);
  }
};
